import {jsxs as _jsxs, jsx as _jsx} from "react/jsx-runtime"; function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import * as Toast from '@radix-ui/react-toast'
import {AnimatePresence, motion} from 'motion/react'
import {memo, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router'
import useAtmosphere from '../hooks/useAtmosphere'
import useBreakpoint from '../hooks/useBreakpoint'
import useEventCallback from '../hooks/useEventCallback'
import {Breakpoint, NavSidebar, ZIndex} from '../types/constEnums'
import clientTempId from '../utils/relay/clientTempId'
import SnackbarMessage from './SnackbarMessage'

const MAX_SNACKS = 1

 



















const Snackbar = memo(() => {
  const snackQueueRef = useRef([])
  const [activeSnacks, setActiveSnacks] = useState([])
  const activeSnackKeysRef = useRef(new Set())
  const atmosphere = useAtmosphere()
  const location = useLocation()
  const hasSidebar =
    location.pathname.startsWith('/meet/') || !!location.pathname.match(/\/meet\/.*\/responses/g)
  const isDesktop = useBreakpoint(Breakpoint.SIDEBAR_LEFT)
  const hoveredSnackRef = useRef(null)
  const dismissOnLeaveRef = useRef()

  const filterSnacks = useEventCallback((removeFn) => {
    const filterFn = (snack) => !removeFn(snack)
    snackQueueRef.current = snackQueueRef.current.filter(filterFn)
    setActiveSnacks((prev) => {
      const next = prev.filter(filterFn)
      if (next.length !== prev.length) {
        activeSnackKeysRef.current = new Set(next.map((s) => s.key))
        return next
      }
      return prev
    })
  })

  const dismissSnack = useEventCallback((snackToDismiss) => {
    _optionalChain([snackToDismiss, 'access', _ => _.onDismiss, 'optionalCall', _2 => _2()])
    filterSnacks((snack) => snack === snackToDismiss)
  })

  const showSnack = useEventCallback((snack) => {
    activeSnackKeysRef.current.add(snack.key)
    setActiveSnacks((prev) => [...prev, snack])
    if (snack.autoDismiss !== 0) {
      setTimeout(() => {
        if (hoveredSnackRef.current === snack) {
          dismissOnLeaveRef.current = snack
        } else {
          dismissSnack(snack)
        }
      }, snack.autoDismiss * 1000)
    }
    _optionalChain([snack, 'access', _3 => _3.onShow, 'optionalCall', _4 => _4()])
  })

  const onMouseEnter = (snack) => () => {
    hoveredSnackRef.current = snack
  }

  const onMouseLeave = () => {
    if (dismissOnLeaveRef.current) {
      dismissSnack(dismissOnLeaveRef.current)
      dismissOnLeaveRef.current = undefined
    }
    hoveredSnackRef.current = null
  }

  const handleAdd = useEventCallback((snack) => {
    const snackInQueue = snackQueueRef.current.find(({key}) => key === snack.key)
    const snackIsActive = activeSnackKeysRef.current.has(snack.key)
    if (snackInQueue || snackIsActive) return
    if (typeof snack.message !== 'string') {
      console.error(`Bad snack message: ${snack.key}`)
      if (snack.message.message) {
        snack.message = snack.message.message
      } else {
        return
      }
    }
    const keyedSnack = {key: clientTempId(), ...snack}
    if (activeSnacks.length < MAX_SNACKS) {
      showSnack(keyedSnack)
    } else {
      snackQueueRef.current.push(keyedSnack)
    }
  })

  useEffect(() => {
    atmosphere.eventEmitter.on('addSnackbar', handleAdd)
    atmosphere.eventEmitter.on('removeSnackbar', filterSnacks)
    return () => {
      atmosphere.eventEmitter.off('addSnackbar', handleAdd)
      atmosphere.eventEmitter.off('removeSnackbar', filterSnacks)
    }
  }, [])

  useEffect(() => {
    if (snackQueueRef.current.length > 0 && activeSnacks.length < MAX_SNACKS) {
      showSnack(snackQueueRef.current.shift())
    }
  }, [showSnack, activeSnacks])

  return (
    _jsxs(Toast.Provider, { children: [
      _jsx(AnimatePresence, { children: 
        activeSnacks.map((snack) => {
          const dismiss = () => {
            if (snack.noDismissOnClick) return
            dismissSnack(snack)
            _optionalChain([snack, 'access', _5 => _5.onManualDismiss, 'optionalCall', _6 => _6()])
          }
          return (
            _jsx(Toast.Root, {

              asChild: true,
              open: true,
              duration: Infinity,
              onOpenChange: (open) => {
                if (!open) dismissSnack(snack)
              },
 children: 
              _jsx(motion.li, {
                initial: {opacity: 0, y: 20},
                animate: {opacity: 1, y: 0},
                exit: {opacity: 0, y: -20, transition: {duration: 0.15, ease: 'easeOut'}},
                transition: {duration: 0.25, ease: 'easeIn'},
                onMouseEnter: onMouseEnter(snack),
                onMouseLeave: onMouseLeave,
                style: {listStyle: 'none'},
 children: 
                _jsx(SnackbarMessage, {
                  message: snack.message,
                  action: snack.action,
                  secondaryAction: snack.secondaryAction,
                  dismissSnack: dismiss,
                  showDismissButton: snack.showDismissButton,}
                )
              })
            }, snack.key)
          )
        })
      })
      , _jsx(Toast.Viewport, {
        style: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          height: '100vh',
          width: hasSidebar && isDesktop ? `calc(100% + ${NavSidebar.WIDTH}px)` : '100%',
          padding: 8,
          paddingBottom: hasSidebar ? 64 : 8,
          pointerEvents: 'none',
          zIndex: ZIndex.SNACKBAR,
          listStyle: 'none',
          margin: 0,
          outline: 'none'
        },}
      )
    ]})
  )
})

export default Snackbar
